// @flow

import React, { PureComponent } from 'react';
import { createPaginationContainer, graphql } from 'react-relay';
import debounce from 'lodash.debounce';
import type { RouterHistory } from 'react-router';
import { BrowserUtils } from 'shared/utils';
import LoadingMore from 'shared/components/common/LoadingMore';
import EmptySearchResults from 'shared/components/common/EmptySearchResults';
import ReturnsTable from './ReturnsTable';

type Props = {
  history: RouterHistory,
  filtersDirty: boolean,
  onCreateNew: () => void,
  returnOrders: Object,
  relay: {
    environment: Object,
    hasMore: () => boolean,
    isLoading: () => boolean,
    loadMore: (pageSize: number, callback: Function) => void,
  },
};

class PaginatedReturnsContainer extends PureComponent<Props> {
  constructor(props: Props) {
    super(props);
    this.onScrollEndReached = debounce(this.onScrollEndReached, 100);
  }

  componentDidMount = () => {
    document.addEventListener('scroll', this.onScrollEndReached);
  };

  componentWillUnmount = () => {
    document.removeEventListener('scroll', this.onScrollEndReached);
  };

  onScrollEndReached = () => {
    const { relay } = this.props;
    if (BrowserUtils.scrollEndReached(200)) {
      if (relay.hasMore()) {
        relay.loadMore(30);
      }
    }
  };

  render() {
    const {
      history,
      onCreateNew,
      returnOrders,
      relay,
      filtersDirty,
    } = this.props;

    if (
      (!returnOrders.returnOrders.edges || !returnOrders.returnOrders.edges.length) &&
      filtersDirty
    ) {
      return <EmptySearchResults />;
    }

    return (
      <>
        <ReturnsTable
          history={history}
          onCreateNew={onCreateNew}
          returnOrderEdges={returnOrders.returnOrders.edges}
        />
        {relay.hasMore() && relay.isLoading() ? <LoadingMore /> : null}
      </>
    );
  }
}

export default createPaginationContainer(
  PaginatedReturnsContainer,
  {
    returnOrders: graphql`
      fragment PaginatedReturnsContainer_returnOrders on Query
        @argumentDefinitions(
          count: { type: "Int", defaultValue: 30 }
          cursor: { type: "String" }
          search: { type: "String" }
          sortBy: { type: "ReturnOrderSort" }
          statuses: { type: "[ReturnOrderStatus!]" }
          returnTypes: { type: "[ReturnType!]" }
          reasons: { type: "[ReturnReason!]" }
          customerIds: { type: "[ID!]" }
          orderIds: { type: "[ID!]" }
        ) {
        returnOrders(
          first: $count
          after: $cursor
          search: $search
          sortBy: $sortBy
          statuses: $statuses
          returnTypes: $returnTypes
          reasons: $reasons
          customerIds: $customerIds
          orderIds: $orderIds
        ) @connection(key: "PaginatedReturnsContainer_returnOrders", filters: []) {
          edges {
            cursor
            ...ReturnsTable_returnOrderEdges
          }
        }
      }
    `,
  },
  {
    query: graphql`
      query PaginatedReturnsContainerQuery(
        $count: Int!
        $cursor: String
        $search: String
        $sortBy: ReturnOrderSort
        $statuses: [ReturnOrderStatus!]
        $returnTypes: [ReturnType!]
        $reasons: [ReturnReason!]
        $customerIds: [ID!]
        $orderIds: [ID!]
      ) {
        ...PaginatedReturnsContainer_returnOrders
          @arguments(
            count: $count
            cursor: $cursor
            search: $search
            sortBy: $sortBy
            statuses: $statuses
            returnTypes: $returnTypes
            reasons: $reasons
            customerIds: $customerIds
            orderIds: $orderIds
          )
      }
    `,
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }, fragmentVariables) {
      return { ...fragmentVariables, count, cursor };
    },
  },
);
